import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiClient from '../../apiTools/apiClient';
import { selectCurrentUser } from '../../slices/userSlice';
import S3AudioPreview from '../../components/admin/S3AudioPreview';
import {
  FaDatabase,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaTools,
  FaTrash,
  FaSync,
  FaChartBar,
  FaNetworkWired,
  FaSearch,
  FaCopy,
  FaCloud,
  FaFileAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCaretDown,
  FaCaretRight,
  FaMusic,
  FaImage,
  FaFile,
  FaCommentDots,
  FaPlay
} from 'react-icons/fa';

import '../../styles/database-audit.css';

const DatabaseAudit = ({ onClose, embedded = false }) => {
  // Needed to sign an S3 object before it can be played back — see S3AudioPreview.
  const currentUser = useSelector(selectCurrentUser);
  const [activeTab, setActiveTab] = useState('health');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State for different audit data
  const [systemHealth, setSystemHealth] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [brokenChains, setBrokenChains] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [databaseMap, setDatabaseMap] = useState(null);
  const [s3Impact, setS3Impact] = useState(null);
  // { [nodeType]: howManySampleNodesAreRevealed } — see shownFor/showMore.
  const [s3SampleShown, setS3SampleShown] = useState({});

  // State for selected items for deletion
  const [selectedOrphanType, setSelectedOrphanType] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Pagination state for orphans
  const [orphanPage, setOrphanPage] = useState(1);
  const [orphanPageSize, setOrphanPageSize] = useState(20);
  const [orphanNodeTypeFilter, setOrphanNodeTypeFilter] = useState(null);
  const [orphanList, setOrphanList] = useState(null);
  const [expandedS3Resources, setExpandedS3Resources] = useState({});

  // Phase 4D Chunk 1 — :DialogueLine substrate tab state
  // `dialogueLines` is the per-book status table; `dialogueLineActions` is a
  // per-book uuid → { running, result, error } map so we can show inline
  // feedback after a back-fill click without blocking the whole tab.
  const [dialogueLines, setDialogueLines] = useState(null);
  const [dialogueLineActions, setDialogueLineActions] = useState({});
  const [dialogueLineRateFilter, setDialogueLineRateFilter] = useState('all');


  // Load initial data based on active tab
  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const loadTabData = async (tab) => {
    setError(null);
    setLoading(true);

    try {
      switch (tab) {
        case 'health':
          await checkSystemHealth();
          break;
        case 'statistics':
          await loadStatistics();
          break;
        case 'orphans':
          await findOrphans();
          break;
        case 'broken':
          await findBrokenChains();
          break;
        case 'duplicates':
          await findDuplicates();
          break;
        case 'map':
          await loadDatabaseMap();
          break;
        case 's3impact':
          await loadS3Impact();
          break;
        case 'dialogueLines':
          await loadDialogueLineStatus();
          break;
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // API calls using apiClient (automatically includes auth token from localStorage)
  const checkSystemHealth = async () => {
    const response = await apiClient.get('/database-audit/health');
    setSystemHealth(response.data);
  };

  const loadStatistics = async () => {
    const response = await apiClient.get('/database-audit/statistics');
    setStatistics(response.data);
  };

  const findOrphans = async (detailed = false, page = orphanPage, pageSize = orphanPageSize, nodeTypeFilter = orphanNodeTypeFilter) => {
    const params = {
      detailed: detailed,
      page: page,
      pageSize: pageSize
    };

    if (nodeTypeFilter) {
      params.nodeTypeFilter = nodeTypeFilter;
    }

    const response = await apiClient.get('/database-audit/orphans', {}, params);

    if (detailed === true || detailed === 'true') {
      setOrphanList(response.data);
    } else {
      setOrphans(response.data);
    }
  };

  // Load detailed orphan list with pagination
  const loadOrphanList = async (page = 1) => {
    setLoading(true);
    setOrphanPage(page);
    try {
      await findOrphans(true, page, orphanPageSize, orphanNodeTypeFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle S3 resource expansion
  const toggleS3Resource = (orphanId) => {
    setExpandedS3Resources(prev => ({
      ...prev,
      [orphanId]: !prev[orphanId]
    }));
  };

  // How many sample nodes are revealed per node type in the S3 Impact list. Kept per type rather
  // than globally, because expanding one type's list should not silently expand every other.
  const S3_SAMPLE_PAGE = 10;
  const shownFor = (nodeType) => s3SampleShown[nodeType] || S3_SAMPLE_PAGE;
  const showMore = (nodeType) => setS3SampleShown((prev) => ({
    ...prev,
    [nodeType]: (prev[nodeType] || S3_SAMPLE_PAGE) + S3_SAMPLE_PAGE
  }));

  // The one place that decides what counts as audio, so the icon and the play controls can never
  // disagree about a file.
  const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a'];

  const isAudioFile = (filename) => {
    if (!filename) return false;
    return AUDIO_EXTENSIONS.includes(filename.split('.').pop().toLowerCase());
  };

  // Get file type icon
  const getFileTypeIcon = (filename) => {
    if (!filename) return <FaFile />;
    const ext = filename.split('.').pop().toLowerCase();
    if (AUDIO_EXTENSIONS.includes(ext)) return <FaMusic />;
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FaImage />;
    return <FaFile />;
  };

  // Format S3 filename for display
  const formatS3Filename = (s3Resource) => {
    if (s3Resource.filename) return s3Resource.filename;
    if (s3Resource.url) {
      const parts = s3Resource.url.split('/');
      return parts[parts.length - 1] || s3Resource.url;
    }
    return 'Unknown file';
  };

  const findBrokenChains = async () => {
    const response = await apiClient.get('/database-audit/broken-chains');
    setBrokenChains(response.data);
  };

  const findDuplicates = async () => {
    const response = await apiClient.get('/database-audit/duplicates');
    setDuplicates(response.data);
  };

  const loadDatabaseMap = async () => {
    const response = await apiClient.get('/database-audit/map');
    setDatabaseMap(response.data);
  };

  const loadS3Impact = async () => {
    const response = await apiClient.get('/database-audit/orphans-s3-impact');
    setS3Impact(response.data);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Phase 4D Chunk 1 — :DialogueLine substrate
  // ───────────────────────────────────────────────────────────────────────
  const loadDialogueLineStatus = async () => {
    const response = await apiClient.get('/admin/dialogue-lines/status');
    setDialogueLines(response.data);
  };

  const runBackfillForBook = async (bookUuid, bookName) => {
    if (!window.confirm(`Run :DialogueLine back-fill for "${bookName}"?\n\nThis is idempotent — existing lines will be updated, not duplicated.`)) {
      return;
    }
    setDialogueLineActions(prev => ({
      ...prev,
      [bookUuid]: { running: true, result: null, error: null }
    }));
    try {
      const response = await apiClient.post(
        `/admin/dialogue-lines/backfill/${bookUuid}`,
        {}
      );
      setDialogueLineActions(prev => ({
        ...prev,
        [bookUuid]: { running: false, result: response.data, error: null }
      }));
      // Refresh status so the table reflects new counts.
      await loadDialogueLineStatus();
    } catch (err) {
      setDialogueLineActions(prev => ({
        ...prev,
        [bookUuid]: {
          running: false,
          result: null,
          error: err.response?.data?.error || err.message
        }
      }));
    }
  };

  const formatAttributionRate = (rate) => {
    if (rate == null) return '—';
    return `${(rate * 100).toFixed(1)}%`;
  };

  const attributionRateClass = (rate) => {
    if (rate == null) return 'rate-na';
    if (rate >= 0.95) return 'rate-good';
    if (rate >= 0.90) return 'rate-warn';
    return 'rate-bad';
  };


  const repairSystemNodes = async () => {
    if (!window.confirm('This will repair/recreate missing system nodes. Continue?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/database-audit/repair/system-nodes', {});
      setSuccess(`System repair complete: ${response.data.message}`);
      // Reload health check
      await checkSystemHealth();
    } catch (err) {
      setError(`Repair failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteOrphans = async (nodeType) => {
    if (deleteConfirmation !== `DELETE_${nodeType}`) {
      setError(`Please type DELETE_${nodeType} to confirm deletion`);
      return;
    }

    if (!window.confirm(`This will permanently delete all orphaned ${nodeType} nodes. This cannot be undone. Continue?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.delete(
        `/database-audit/orphans/${nodeType}`,
        { nodeType },  // URL params
        { data: { confirmed: true } }  // Config with body data for DELETE
      );
      setSuccess(`Deleted ${response.data.deletedCount} orphaned ${nodeType} nodes`);
      setDeleteConfirmation('');
      // Reload orphans
      await findOrphans();
    } catch (err) {
      setError(`Deletion failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Generate Cypher queries for manual execution
  const generateCypherQuery = (queryType, params = {}) => {
    const queries = {
      restoreUsers: `
// Restore Users root node
MERGE (users:Users {systemNode: true})
SET users.createdAt = COALESCE(users.createdAt, datetime())
SET users.description = 'System root node for all users'
SET users.critical = true
RETURN users`,

      reconnectUsers: `
// Reconnect orphaned users
MATCH (users:Users)
MATCH (u:User)
WHERE NOT (users)-[:HAS_USER]->(u)
CREATE (users)-[:HAS_USER]->(u)
RETURN count(u) as reconnectedCount`,

      findOrphans: `
// Find all orphaned nodes
MATCH (n)
WHERE NOT (n)--()
RETURN labels(n) as NodeType, count(n) as Count
ORDER BY Count DESC`,

      deleteSpecificOrphans: `
// Delete specific type of orphans
MATCH (n:${params.nodeType || 'NodeType'})
WHERE NOT (n)--()
DETACH DELETE n
RETURN count(n) as deletedCount`
    };

    return queries[queryType] || '';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Query copied to clipboard');
    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <div className={embedded ? 'database-audit-embedded' : 'database-audit-modal'}>
      <div className="database-audit-container">
        <div className="audit-header">
          <h2><FaDatabase /> Database Audit & Maintenance</h2>
          {!embedded && <button className="close-button" onClick={onClose}>×</button>}
        </div>

        {error && (
          <div className="alert alert-danger">
            <FaTimesCircle /> {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <FaCheckCircle /> {success}
          </div>
        )}

        <div className="audit-tabs">
          <button
            className={`tab ${activeTab === 'health' ? 'active' : ''}`}
            onClick={() => setActiveTab('health')}
          >
            <FaCheckCircle /> System Health
          </button>
          <button
            className={`tab ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            <FaChartBar /> Statistics
          </button>
          <button
            className={`tab ${activeTab === 'orphans' ? 'active' : ''}`}
            onClick={() => setActiveTab('orphans')}
          >
            <FaExclamationTriangle /> Orphans
          </button>
          <button
            className={`tab ${activeTab === 'broken' ? 'active' : ''}`}
            onClick={() => setActiveTab('broken')}
          >
            <FaTimesCircle /> Broken Chains
          </button>
          <button
            className={`tab ${activeTab === 'duplicates' ? 'active' : ''}`}
            onClick={() => setActiveTab('duplicates')}
          >
            <FaCopy /> Duplicates
          </button>
          <button
            className={`tab ${activeTab === 'map' ? 'active' : ''}`}
            onClick={() => setActiveTab('map')}
          >
            <FaNetworkWired /> Database Map
          </button>
          <button
            className={`tab ${activeTab === 's3impact' ? 'active' : ''}`}
            onClick={() => setActiveTab('s3impact')}
          >
            <FaCloud /> S3 Impact
          </button>
          <button
            className={`tab ${activeTab === 'dialogueLines' ? 'active' : ''}`}
            onClick={() => setActiveTab('dialogueLines')}
            title="Phase 4D :DialogueLine substrate — per-book back-fill status"
          >
            <FaCommentDots /> Dialogue Lines
          </button>
        </div>


        <div className="audit-content">
          {loading && <div className="loading">Loading...</div>}

          {/* System Health Tab */}
          {activeTab === 'health' && systemHealth && !loading && (
            <div className="health-panel">
              <h3>System Nodes Status</h3>

              <div className="health-status">
                <div className={`status-item ${systemHealth.usersNode?.exists ? 'healthy' : 'critical'}`}>
                  <span className="status-label">Users Root Node:</span>
                  <span className="status-value">
                    {systemHealth.usersNode?.exists ? (
                      <><FaCheckCircle className="icon-success" /> EXISTS</>
                    ) : (
                      <><FaTimesCircle className="icon-danger" /> MISSING</>
                    )}
                  </span>
                </div>

                <div className={`status-item ${systemHealth.opportunityZone?.exists ? 'healthy' : 'critical'}`}>
                  <span className="status-label">OpportunityZone Node:</span>
                  <span className="status-value">
                    {systemHealth.opportunityZone?.exists ? (
                      <><FaCheckCircle className="icon-success" /> EXISTS</>
                    ) : (
                      <><FaTimesCircle className="icon-danger" /> MISSING</>
                    )}
                  </span>
                </div>

                <div className={`status-item ${systemHealth.healthy ? 'healthy' : 'critical'}`}>
                  <span className="status-label">Overall Health:</span>
                  <span className="status-value">
                    {systemHealth.healthy ? (
                      <><FaCheckCircle className="icon-success" /> HEALTHY</>
                    ) : (
                      <><FaExclamationTriangle className="icon-warning" /> NEEDS REPAIR</>
                    )}
                  </span>
                </div>
              </div>

              {!systemHealth.healthy && (
                <div className="repair-section">
                  <h4>Repair Options</h4>
                  <button
                    className="btn btn-warning"
                    onClick={repairSystemNodes}
                    disabled={loading}
                  >
                    <FaTools /> Repair System Nodes
                  </button>

                  <div className="manual-queries">
                    <h5>Manual Cypher Queries:</h5>

                    {!systemHealth.usersNode?.exists && (
                      <div className="query-block">
                        <p>Restore Users node:</p>
                        <pre>{generateCypherQuery('restoreUsers')}</pre>
                        <button
                          className="btn btn-sm"
                          onClick={() => copyToClipboard(generateCypherQuery('restoreUsers'))}
                        >
                          <FaCopy /> Copy
                        </button>
                      </div>
                    )}

                    <div className="query-block">
                      <p>Reconnect orphaned users:</p>
                      <pre>{generateCypherQuery('reconnectUsers')}</pre>
                      <button
                        className="btn btn-sm"
                        onClick={() => copyToClipboard(generateCypherQuery('reconnectUsers'))}
                      >
                        <FaCopy /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === 'statistics' && statistics && !loading && (
            <div className="statistics-panel">
              <h3>Database Statistics</h3>

              {/* 3-Column Layout with 2 fields each */}
              <div className="stats-three-column-grid">
                {statistics.statistics?.map((stat, index) => (
                  <div key={index} className="stat-item-horizontal">
                    <span className="stat-label">{stat.nodeType.join(', ')}</span>
                    <span className="stat-value">{stat.count.toLocaleString()}</span>
                  </div>
                ))}
                {/* Add total as the last item */}
                {statistics.statistics && statistics.statistics.length > 0 && (
                  <div className="stat-item-horizontal stat-total">
                    <span className="stat-label">Total Nodes</span>
                    <span className="stat-value">
                      {statistics.statistics
                        .reduce((sum, stat) => sum + stat.count, 0)
                        .toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Orphans Tab */}
          {activeTab === 'orphans' && orphans && !loading && (
            <div className="orphans-panel">
              <h3>Orphaned Nodes (Total: {orphans.totalOrphans})</h3>

              {/* Summary by node type */}
              <div className="orphan-summary">
                {orphans.orphans?.map((orphan, index) => (
                  <div key={index} className="summary-badge">
                    <span className="badge-label">{orphan.nodeType}: </span>
                    <span className="badge-count">{orphan.count}</span>
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div className="orphan-controls">
                <div className="control-group">
                  <label>Filter by Type:</label>
                  <select
                    value={orphanNodeTypeFilter || ''}
                    onChange={(e) => {
                      setOrphanNodeTypeFilter(e.target.value || null);
                      setOrphanPage(1); // Reset to page 1 when filtering
                    }}
                    className="filter-select"
                  >
                    <option value="">All Types</option>
                    {orphans.orphans?.map(orphan => (
                      <option key={orphan.nodeType} value={orphan.nodeType}>
                        {orphan.nodeType} ({orphan.count})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="control-group">
                  <label>Items per page:</label>
                  <select
                    value={orphanPageSize}
                    onChange={(e) => {
                      setOrphanPageSize(parseInt(e.target.value));
                      setOrphanPage(1); // Reset to page 1
                    }}
                    className="pagesize-select"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={() => loadOrphanList(1)}
                  disabled={loading}
                >
                  <FaSearch /> Load Orphan List
                </button>
              </div>

              {/* Orphan List Table */}
              {orphanList && orphanList.orphanList && (
                <>
                  <div className="orphan-table-container">
                    <table className="orphan-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Identifier</th>
                          <th>S3 Resources</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphanList.orphanList.map((orphan, index) => (
                          <React.Fragment key={orphan.uuid || index}>
                            <tr>
                              <td className="node-type">{orphan.nodeType}</td>
                              <td className="identifier">
                                <div>{orphan.identifier}</div>
                                {orphan.properties.email && (
                                  <div className="sub-info">Email: {orphan.properties.email}</div>
                                )}
                                <div className="uuid-info">UUID: {orphan.uuid}</div>
                              </td>
                              <td className="s3-resources">
                                {orphan.s3Resources && orphan.s3Resources.length > 0 ? (
                                  <button
                                    className="s3-expand-btn"
                                    onClick={() => toggleS3Resource(orphan.uuid)}
                                  >
                                    {expandedS3Resources[orphan.uuid] ? <FaCaretDown /> : <FaCaretRight />}
                                    {orphan.s3Resources.length} file{orphan.s3Resources.length !== 1 ? 's' : ''}
                                  </button>
                                ) : (
                                  <span className="no-s3">No S3 files</span>
                                )}
                              </td>
                              <td className="created-date">
                                {orphan.createdAt
                                  ? new Date(orphan.createdAt).toLocaleDateString()
                                  : 'Unknown'}
                              </td>
                              <td className="actions">
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => {
                                    if (window.confirm(`Delete orphaned ${orphan.nodeType}: ${orphan.identifier}?`)) {
                                      // Individual delete would be implemented here
                                      console.log('Delete individual orphan:', orphan.uuid);
                                    }
                                  }}
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                            {/* Expandable S3 Resources Row */}
                            {expandedS3Resources[orphan.uuid] && orphan.s3Resources && (
                              <tr className="s3-details-row">
                                <td colSpan="5">
                                  <div className="s3-details">
                                    <h5>S3 Resources:</h5>
                                    <ul className="s3-list">
                                      {orphan.s3Resources.map((resource, idx) => (
                                        <li key={idx}>
                                          <span className="s3-icon">
                                            {getFileTypeIcon(resource.filename)}
                                          </span>
                                          <span className="s3-field">{resource.field}:</span>
                                          <code className="s3-filename">
                                            {formatS3Filename(resource)}
                                          </code>
                                          {resource.isShared && (
                                            <span className="shared-warning">⚠️ SHARED</span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {orphanList.pagination && (
                    <div className="pagination-controls">
                      <div className="pagination-info">
                        Showing {((orphanList.pagination.currentPage - 1) * orphanList.pagination.pageSize) + 1} - {
                          Math.min(
                            orphanList.pagination.currentPage * orphanList.pagination.pageSize,
                            orphanList.pagination.totalItems
                          )
                        } of {orphanList.pagination.totalItems} orphans
                      </div>
                      <div className="pagination-buttons">
                        <button
                          className="btn btn-sm"
                          onClick={() => loadOrphanList(1)}
                          disabled={orphanList.pagination.currentPage === 1 || loading}
                        >
                          First
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => loadOrphanList(orphanList.pagination.currentPage - 1)}
                          disabled={!orphanList.pagination.hasPrevious || loading}
                        >
                          <FaChevronLeft /> Previous
                        </button>

                        <span className="page-indicator">
                          Page {orphanList.pagination.currentPage} of {orphanList.pagination.totalPages}
                        </span>

                        <button
                          className="btn btn-sm"
                          onClick={() => loadOrphanList(orphanList.pagination.currentPage + 1)}
                          disabled={!orphanList.pagination.hasNext || loading}
                        >
                          Next <FaChevronRight />
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => loadOrphanList(orphanList.pagination.totalPages)}
                          disabled={orphanList.pagination.currentPage === orphanList.pagination.totalPages || loading}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Bulk Delete Section */}
              <div className="bulk-delete-section">
                <h4>Bulk Delete Options</h4>
                <div className="bulk-delete-grid">
                  {orphans.orphans?.map((orphan, index) => (
                    <div key={index} className="bulk-delete-item">
                      <div className="delete-header">
                        <span>{orphan.nodeType}</span>
                        <span className="count">{orphan.count} nodes</span>
                      </div>
                      <input
                        type="text"
                        placeholder={`Type DELETE_${orphan.nodeType} to confirm`}
                        value={selectedOrphanType === orphan.nodeType ? deleteConfirmation : ''}
                        onChange={(e) => {
                          setSelectedOrphanType(orphan.nodeType);
                          setDeleteConfirmation(e.target.value);
                        }}
                        className="confirmation-input"
                      />
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteOrphans(orphan.nodeType)}
                        disabled={deleteConfirmation !== `DELETE_${orphan.nodeType}`}
                      >
                        <FaTrash /> Delete All {orphan.nodeType}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Manual Queries */}
              <div className="manual-queries">
                <h5>Manual Query:</h5>
                <div className="query-block">
                  <p>Find all orphans:</p>
                  <pre>{generateCypherQuery('findOrphans')}</pre>
                  <button
                    className="btn btn-sm"
                    onClick={() => copyToClipboard(generateCypherQuery('findOrphans'))}
                  >
                    <FaCopy /> Copy
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Broken Chains Tab */}
          {activeTab === 'broken' && brokenChains && !loading && (
            <div className="broken-panel">
              <h3>Broken Relationship Chains</h3>

              <div className="broken-list">
                {brokenChains.brokenChains?.map((item, index) => (
                  <div key={index} className="broken-item">
                    <h4>{item.nodeType}: {item.count} nodes</h4>
                    {item.samples && item.samples.length > 0 && (
                      <ul>
                        {item.samples.map((sample, idx) => (
                          <li key={idx}>{sample.name} (UUID: {sample.uuid})</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Duplicates Tab */}
          {activeTab === 'duplicates' && duplicates && !loading && (
            <div className="duplicates-panel">
              <h3>Duplicate Nodes</h3>

              <div className="duplicate-list">
                {duplicates.duplicates?.map((dup, index) => (
                  <div key={index} className="duplicate-item">
                    <h4>{dup.nodeType}: {dup.identifier}</h4>
                    <p>Count: {dup.count}</p>
                    <ul>
                      {dup.instances.map((instance, idx) => (
                        <li key={idx}>
                          {JSON.stringify(instance, null, 2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* S3 Impact Analysis Tab */}
          {activeTab === 's3impact' && s3Impact && !loading && (
            <div className="s3-impact-panel">
              <h3>S3 Impact Analysis for Orphaned Nodes</h3>

              {/* Summary */}
              <div className="s3-summary">
                <div className="summary-card">
                  <FaDatabase className="summary-icon" />
                  <div>
                    <span className="summary-label">Total Orphaned Nodes</span>
                    <span className="summary-value">{s3Impact.summary?.totalOrphanedNodes || 0}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <FaCloud className="summary-icon" />
                  <div>
                    <span className="summary-label">Total S3 Files</span>
                    <span className="summary-value">{s3Impact.summary?.totalS3Files || 0}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <FaFileAlt className="summary-icon" />
                  <div>
                    <span className="summary-label">Node Types with S3</span>
                    <span className="summary-value">{s3Impact.summary?.nodeTypesWithS3 || 0}</span>
                  </div>
                </div>
              </div>

              {/* S3 Impact Details */}
              <div className="s3-impact-list">
                {s3Impact.s3Impact?.map((nodeTypeAnalysis, index) => (
                  <div key={index} className="s3-node-type-card">
                    <div className="s3-node-header">
                      <h4>{nodeTypeAnalysis.nodeType}</h4>
                      <div className="s3-node-stats">
                        <span className="badge">{nodeTypeAnalysis.orphanCount} orphaned</span>
                        <span className="badge badge-warning">{nodeTypeAnalysis.s3Summary?.totalFiles || 0} S3 files</span>
                        {nodeTypeAnalysis.nodesWithoutS3 > 0 && (
                          <span className="badge badge-missing">{nodeTypeAnalysis.nodesWithoutS3} missing images</span>
                        )}
                      </div>
                    </div>

                    {/* File Type Summary */}
                    {nodeTypeAnalysis.s3Summary?.fileTypes && Object.keys(nodeTypeAnalysis.s3Summary.fileTypes).length > 0 && (
                      <div className="s3-file-types">
                        <h5>File Types:</h5>
                        <div className="file-type-grid">
                          {Object.entries(nodeTypeAnalysis.s3Summary.fileTypes).map(([ext, count]) => (
                            <span key={ext} className="file-type-badge">
                              .{ext}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* S3 Paths */}
                    {nodeTypeAnalysis.s3Summary?.uniquePaths && nodeTypeAnalysis.s3Summary.uniquePaths.length > 0 && (
                      <div className="s3-paths">
                        <h5>S3 Paths: (Showing {Math.min(10, nodeTypeAnalysis.s3Summary.uniquePaths.length)} of {nodeTypeAnalysis.s3Summary.uniquePaths.length} total)</h5>
                        <ul className="path-list">
                          {nodeTypeAnalysis.s3Summary.uniquePaths.slice(0, 10).map((path, idx) => (
                            <li key={idx}>
                              <code>{path}</code>
                            </li>
                          ))}
                          {nodeTypeAnalysis.s3Summary.uniquePaths.length > 10 && (
                            <li className="more-paths">
                              ... and {nodeTypeAnalysis.s3Summary.uniquePaths.length - 10} more paths not shown
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Sample Resources */}
                    {nodeTypeAnalysis.s3Resources && nodeTypeAnalysis.s3Resources.length > 0 && (
                      <details className="s3-resources-details">
                        <summary>View Sample Nodes with S3 ({nodeTypeAnalysis.s3Resources.length} total)</summary>
                        <div className="s3-resources-list">
                          <div className="sample-count-info">
                            Showing {Math.min(shownFor(nodeTypeAnalysis.nodeType), nodeTypeAnalysis.s3Resources.length)} of {nodeTypeAnalysis.s3Resources.length} nodes with S3 resources
                          </div>
                          {nodeTypeAnalysis.s3Resources.slice(0, shownFor(nodeTypeAnalysis.nodeType)).map((resource, idx) => (
                            <div key={idx} className="s3-resource-item">
                              <strong>{idx + 1}. {resource.nodeName}</strong>
                              <ul>
                                {resource.s3Urls.map((s3Url, urlIdx) => {
                                  const label = s3Url.filename || s3Url.url;
                                  return (
                                    // Flex row so the controls land in a fixed right-hand column.
                                    // Filenames vary wildly in length, so trailing them left the
                                    // play buttons ragged and unscannable down the list.
                                    <li key={urlIdx} className="s3-url-row">
                                      <span className="s3-field">{s3Url.field}:</span>
                                      <code
                                        className={s3Url.isShared ? 'shared-resource s3-url-label' : 's3-url-label'}
                                        title={s3Url.url}
                                      >
                                        {label}
                                      </code>
                                      {s3Url.isShared && <span className="shared-badge">SHARED</span>}
                                      {/* A filename cannot tell you whether an orphan is a
                                          discarded take or a finished render. Four seconds of it
                                          can — and a file that refuses to play says something too. */}
                                      <span className="s3-url-actions">
                                        {isAudioFile(label) && (s3Url.key || s3Url.url) && (
                                          <S3AudioPreview
                                            // Prefer the PARSED key over the raw stored value: the
                                            // property may hold `s3://bucket/key`, a bare key, or a
                                            // full URL, and parseS3Url has already reduced all
                                            // three to one form.
                                            url={s3Url.key || s3Url.url}
                                            userId={currentUser?.userId}
                                          />
                                        )}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}

                          {/* "more" rather than a hard cap: the cap was invisible policy — the
                              list said 10 of 340 and offered no way to see the 11th. */}
                          {nodeTypeAnalysis.s3Resources.length > shownFor(nodeTypeAnalysis.nodeType) && (
                            <button
                              type="button"
                              className="btn btn-secondary s3-show-more"
                              onClick={() => showMore(nodeTypeAnalysis.nodeType)}
                            >
                              More… ({nodeTypeAnalysis.s3Resources.length - shownFor(nodeTypeAnalysis.nodeType)} not shown)
                            </button>
                          )}
                        </div>
                      </details>
                    )}

                    {/* Deletion Warning */}
                    {nodeTypeAnalysis.s3Resources && nodeTypeAnalysis.s3Resources.some(r => r.s3Urls.some(u => u.isShared)) && (
                      <div className="alert alert-warning">
                        <FaExclamationTriangle /> Warning: Some S3 files are shared with active nodes. Deletion would affect active data.
                      </div>
                    )}
                  </div>
                ))}

                {(!s3Impact.s3Impact || s3Impact.s3Impact.length === 0) && (
                  <div className="no-s3-impact">
                    <p>No orphaned nodes with S3 resources found.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dialogue Lines Tab — Phase 4D Chunk 1 :DialogueLine substrate */}
          {activeTab === 'dialogueLines' && dialogueLines && !loading && (() => {
            const rows = (dialogueLines.books || []).filter(b => {
              if (dialogueLineRateFilter === 'all') return true;
              if (dialogueLineRateFilter === 'belowGood') {
                return b.attributionRate != null && b.attributionRate < 0.95;
              }
              if (dialogueLineRateFilter === 'noLines') {
                return b.totalLines === 0 && b.hasProcessedItems;
              }
              if (dialogueLineRateFilter === 'noProcessed') {
                return !b.hasProcessedItems;
              }
              return true;
            });

            const totalLinesAcrossBooks = (dialogueLines.books || [])
              .reduce((sum, b) => sum + (b.totalLines || 0), 0);
            const totalAttributedAcrossBooks = (dialogueLines.books || [])
              .reduce((sum, b) => sum + (b.attributedLines || 0), 0);
            const overallRate = totalLinesAcrossBooks > 0
              ? totalAttributedAcrossBooks / totalLinesAcrossBooks
              : null;

            return (
              <div className="dialogue-lines-panel">
                <h3>
                  <FaCommentDots /> :DialogueLine Substrate (Phase 4D)
                </h3>
                <p className="panel-subtitle">
                  Per-book status of the <code>:DialogueLine</code> nodes that back the
                  voice-actor character-context hover panel. Books ingested before Phase
                  4D need a one-time back-fill; re-running is idempotent (MERGE on
                  <code>{`(bookId, lineId)`}</code>).
                </p>

                {/* Summary cards */}
                <div className="s3-summary">
                  <div className="summary-card">
                    <FaDatabase className="summary-icon" />
                    <div>
                      <span className="summary-label">Books</span>
                      <span className="summary-value">{dialogueLines.total || 0}</span>
                    </div>
                  </div>
                  <div className="summary-card">
                    <FaCommentDots className="summary-icon" />
                    <div>
                      <span className="summary-label">Total :DialogueLine nodes</span>
                      <span className="summary-value">
                        {totalLinesAcrossBooks.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="summary-card">
                    <FaCheckCircle className="summary-icon" />
                    <div>
                      <span className="summary-label">Overall attribution rate</span>
                      <span className={`summary-value ${attributionRateClass(overallRate)}`}>
                        {formatAttributionRate(overallRate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Filter */}
                <div className="orphan-controls">
                  <div className="control-group">
                    <label>Show:</label>
                    <select
                      value={dialogueLineRateFilter}
                      onChange={(e) => setDialogueLineRateFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All books</option>
                      <option value="belowGood">Attribution rate &lt; 95%</option>
                      <option value="noLines">No lines yet (back-fill needed)</option>
                      <option value="noProcessed">No processed items (cannot back-fill)</option>
                    </select>
                  </div>
                </div>

                {/* Books table */}
                <div className="orphan-table-container">
                  <table className="orphan-table">
                    <thead>
                      <tr>
                        <th>Book</th>
                        <th>LitGroup</th>
                        <th style={{ textAlign: 'right' }}>Lines</th>
                        <th style={{ textAlign: 'right' }}>Attributed</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                        <th style={{ textAlign: 'right' }}>Characters</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>
                            No books match this filter.
                          </td>
                        </tr>
                      )}
                      {rows.map((b) => {
                        const action = dialogueLineActions[b.bookUuid] || {};
                        return (
                          <React.Fragment key={b.bookUuid}>
                            <tr>
                              <td className="identifier">
                                <div>{b.bookName}</div>
                                <div className="uuid-info">UUID: {b.bookUuid}</div>
                              </td>
                              <td>{b.litGroupName || <span className="no-s3">—</span>}</td>
                              <td style={{ textAlign: 'right' }}>
                                {b.totalLines.toLocaleString()}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {b.attributedLines.toLocaleString()}
                              </td>
                              <td
                                style={{ textAlign: 'right' }}
                                className={attributionRateClass(b.attributionRate)}
                              >
                                {formatAttributionRate(b.attributionRate)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {b.charactersOnBook}
                              </td>
                              <td>
                                {!b.hasProcessedItems ? (
                                  <span className="no-s3" title="No :ProcessedItem found — cannot back-fill">
                                    No script
                                  </span>
                                ) : action.running ? (
                                  <button className="btn btn-sm" disabled>
                                    <FaSync className="spin" /> Running…
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => runBackfillForBook(b.bookUuid, b.bookName)}
                                    title="Run the :DialogueLine back-fill helper for this book"
                                  >
                                    <FaPlay /> {b.totalLines > 0 ? 'Re-run' : 'Back-fill'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {/* Inline result / error row */}
                            {(action.result || action.error) && (
                              <tr className="s3-details-row">
                                <td colSpan="7">
                                  {action.error && (
                                    <div className="alert alert-danger" style={{ margin: 0 }}>
                                      <FaTimesCircle /> {action.error}
                                    </div>
                                  )}
                                  {action.result && (
                                    <div className="s3-details">
                                      <div>
                                        <FaCheckCircle className="icon-success" />{' '}
                                        Back-fill complete in {action.result.took}ms
                                        {' '}(write {action.result.writeTook}ms)
                                        {' — '}
                                        <strong>created</strong> {action.result.created}
                                        {', '}
                                        <strong>updated</strong> {action.result.updated}
                                        {', '}
                                        <strong>attempted</strong> {action.result.attempted}
                                        {', '}
                                        <strong>characters resolved</strong> {action.result.charactersResolved}
                                      </div>
                                      <div style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
                                        Script: <code>{action.result.script?.fileName}</code>
                                        {action.result.script?.version != null && (
                                          <> (v{action.result.script.version})</>
                                        )}
                                      </div>
                                      {action.result.unresolvedSpeakers
                                        && action.result.unresolvedSpeakers.length > 0 && (
                                          <details style={{ marginTop: '0.5rem' }}>
                                            <summary>
                                              ⚠ Unresolved speakers:{' '}
                                              {action.result.unresolvedSpeakers.length}
                                            </summary>
                                            <ul className="s3-list">
                                              {action.result.unresolvedSpeakers.map((s, i) => (
                                                <li key={i}>
                                                  <code>{s.speakerRaw}</code>{' '}
                                                  <span className="no-s3">
                                                    (×{s.count}, e.g. line {s.firstLineIndex})
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </details>
                                        )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="manual-queries" style={{ marginTop: '1rem' }}>
                  <h5>Operator notes</h5>
                  <ul>
                    <li>
                      Rate ≥ 95% → green; 90–95% → amber; &lt; 90% → red. Stop and surface
                      the unresolved-speakers list to the operator if a book lands below 90%
                      — that's a name-normalization issue (see substrate doc §5.3).
                    </li>
                    <li>
                      Re-running back-fill is safe — MERGE on{' '}
                      <code>{`(bookId, lineId)`}</code> means existing rows are updated, not
                      duplicated.
                    </li>
                    <li>
                      Books with no script have never had a processed-version produced —
                      run ingest first, then return here.
                    </li>
                  </ul>
                </div>
              </div>
            );
          })()}

          {/* Database Map Tab */}
          {activeTab === 'map' && databaseMap && !loading && (

            <div className="map-panel">
              <h3>Database Structure Map</h3>

              <div className="map-summary">
                <div className="summary-item">
                  <span>Total Node Types:</span>
                  <strong>{databaseMap.summary?.totalNodeTypes}</strong>
                </div>
                <div className="summary-item">
                  <span>Total Nodes:</span>
                  <strong>{databaseMap.summary?.totalNodes?.toLocaleString()}</strong>
                </div>
                <div className="summary-item">
                  <span>Total Relationship Types:</span>
                  <strong>{databaseMap.summary?.totalRelationshipTypes}</strong>
                </div>
                <div className="summary-item">
                  <span>Total Relationships:</span>
                  <strong>{databaseMap.summary?.totalRelationships?.toLocaleString()}</strong>
                </div>
              </div>

              <div className="map-details">
                <div className="map-section">
                  <h4>Node Types</h4>
                  <table className="map-table">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {databaseMap.nodes?.map((node, idx) => (
                        <tr key={idx}>
                          <td>{node.label}</td>
                          <td>{node.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="map-section">
                  <h4>Relationship Types</h4>
                  <table className="map-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {databaseMap.relationships?.map((rel, idx) => (
                        <tr key={idx}>
                          <td>{rel.type}</td>
                          <td>{rel.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="audit-footer">
          <button
            className="btn btn-secondary"
            onClick={() => loadTabData(activeTab)}
            disabled={loading}
          >
            <FaSync /> Refresh
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseAudit;
